import { useEffect, useRef } from 'react';

const benefits = [
  {
    icon: 'ri-map-pin-2-line',
    title: 'Local Expertise',
    desc: 'Certified inspectors serving Baytown, East Harris, Liberty, and Chambers Counties with fast, reliable scheduling.',
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Licensed Professionals',
    desc: 'Every inspector is fully licensed, vetted, and follows rigorous industry standards on every evaluation.',
  },
  {
    icon: 'ri-timer-flash-line',
    title: 'Fast Turnaround',
    desc: 'Same-day and next-day scheduling available in most areas. Reports delivered quickly after completion.',
  },
  {
    icon: 'ri-file-list-3-line',
    title: 'Clear Reporting',
    desc: 'Detailed digital reports with photos, findings, and actionable recommendations—easy to understand.',
  },
  {
    icon: 'ri-star-line',
    title: 'Reliable Service Experience',
    desc: 'Trusted by buyers, sellers, agents, and investors for accurate, professional inspection results.',
  },
  {
    icon: 'ri-customer-service-2-line',
    title: 'Dedicated Support',
    desc: 'Our team is available to answer questions before, during, and after your inspection.',
  },
];

export default function WhyChooseUs() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const items = sectionRef.current?.querySelectorAll('.why-item');
    if (!items) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            (entry.target as HTMLElement).style.opacity = '1';
            (entry.target as HTMLElement).style.transform = 'translateY(0)';
          }
        });
      },
      { threshold: 0.1 }
    );
    items.forEach((item, i) => {
      (item as HTMLElement).style.opacity = '0';
      (item as HTMLElement).style.transform = 'translateY(18px)';
      (item as HTMLElement).style.transition = `opacity 0.5s ease ${i * 0.08}s, transform 0.5s ease ${i * 0.08}s`;
      observer.observe(item);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col lg:flex-row gap-16 items-start">
          {/* Left: heading */}
          <div className="w-full lg:w-2/5 lg:sticky lg:top-28">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-px bg-[#C9A961]"></div>
              <span className="text-[#C9A961] text-xs font-semibold uppercase tracking-[0.2em]">Why Us</span>
            </div>
            <h2
              className="text-4xl md:text-5xl font-bold text-[#1A1A1A] leading-tight mb-6"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Why Clients Choose
              <br />
              <span className="text-[#C9A961]">Inspectors.com</span>
            </h2>
            <p className="text-[#1A1A1A]/55 text-base leading-relaxed mb-8">
              We serve the greater Houston area with certified professionals who make property inspections fast, reliable, and stress-free — for every client, every time.
            </p>
            {/* Inspector photo — no text overlay, just clean image */}
            <div className="w-full h-64 rounded-xl overflow-hidden relative">
              <img
                src="https://inspectors.com/wp-content/uploads/2022/01/02.png"
                alt="Professional certified home inspector"
                className="w-full h-full object-cover object-top"
              />
              {/* Subtle bottom fade for depth only */}
              <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/30 to-transparent rounded-b-xl"></div>
            </div>
          </div>

          {/* Right: benefits grid */}
          <div className="w-full lg:w-3/5 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {benefits.map((b, i) => (
              <div key={i} className="why-item flex gap-4 p-6 rounded-xl bg-[#F9F8F6]">
                <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-[#C9A961]/12 rounded-lg mt-0.5">
                  <i className={`${b.icon} text-[#C9A961] text-lg`}></i>
                </div>
                <div>
                  <h4 className="text-sm font-bold text-[#1A1A1A] mb-1.5">{b.title}</h4>
                  <p className="text-sm text-[#1A1A1A]/58 leading-relaxed">{b.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
